import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";
import { useAuth } from "@/hooks/useAuth";
import type { Tables } from "@/integrations/neon/types";

export type CurrentPerson = Tables<"people">;

type ZeroCurrentPersonRow = {
  id: string;
  userID?: string | null;
  companyID?: string | null;
  firstName: string;
  lastName: string;
  number?: string | null;
  function?: string | null;
  phone?: string | null;
  email?: string | null;
  personType: CurrentPerson["person_type"];
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  deletedAt?: number | string | null;
  version?: number | null;
  isAdmin?: boolean | null;
  clockifyUserID?: string | null;
  rentmanID?: number | null;
  allergies?: string | null;
  remarks?: string | null;
  notes?: string | null;
  allergiesNotes?: string | null;
  unityUsername?: string | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyPerson(row: ZeroCurrentPersonRow): CurrentPerson {
  return {
    id: row.id,
    user_id: row.userID ?? null,
    company_id: row.companyID ?? null,
    first_name: row.firstName,
    last_name: row.lastName,
    number: row.number ?? null,
    function: row.function ?? null,
    phone: row.phone ?? null,
    email: row.email ?? null,
    person_type: row.personType,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    deleted_at: row.deletedAt ? toLegacyTimestamp(row.deletedAt) : null,
    version: row.version ?? null,
    is_admin: row.isAdmin ?? false,
    clockify_user_id: row.clockifyUserID ?? null,
    rentman_id: row.rentmanID ?? null,
    allergies: row.allergies ?? null,
    remarks: row.remarks ?? null,
    notes: row.notes ?? null,
    allergies_notes: row.allergiesNotes ?? null,
    unity_username: row.unityUsername ?? null,
  };
}

export function useCurrentPerson() {
  const { user } = useAuth();
  const query = user?.id
    ? appQueries.people.byUserID({ userID: user.id })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!user?.id });

  return {
    data: data ? toLegacyPerson(data as ZeroCurrentPersonRow) : null,
    isLoading: !!user?.id && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}
